import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import api from "@/lib/api";
import { BellRinging, Warning, XCircle, ArrowRight } from "@phosphor-icons/react";

const SECTIONS = [
  { key: "expired", title: "Expired", subtitle: "Immediate action required", color: "rose", icon: XCircle },
  { key: "30", title: "Expiring in 30 days", subtitle: "Critical priority", color: "rose", icon: BellRinging },
  { key: "60", title: "Expiring in 60 days", subtitle: "Elevated priority", color: "amber", icon: Warning },
  { key: "90", title: "Expiring in 90 days", subtitle: "Standard follow-up", color: "amber", icon: BellRinging },
];

const COLORS = {
  rose: { border: "border-rose-500/25", bg: "bg-rose-500/5", text: "text-rose-300", dot: "bg-rose-400" },
  amber: { border: "border-amber-500/25", bg: "bg-amber-500/5", text: "text-amber-300", dot: "bg-amber-400" },
};

export default function AlertsPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/alerts").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 text-slate-400">Loading alerts…</div>;

  const total = Object.values(data).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <div className="mb-8">
        <div className="font-label text-orange-400 mb-2">EXPIRATION ALERTS</div>
        <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Attention Queue</h1>
        <p className="text-slate-400 mt-2">
          {total === 0 ? "Everything current. Nothing to worry about." : `${total} credentials need action.`}
        </p>
      </div>

      {total === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-slate-900 p-16 text-center">
          <BellRinging size={48} weight="duotone" className="text-orange-400 mx-auto mb-4" />
          <div className="text-xl font-display font-black tracking-tight">All caught up</div>
          <div className="text-slate-500 text-sm mt-2">No credentials expiring within 90 days.</div>
        </div>
      ) : (
        <div className="space-y-8">
          {SECTIONS.map((section) => {
            const items = data[section.key] || [];
            if (items.length === 0) return null;
            const c = COLORS[section.color];
            const Icon = section.icon;
            return (
              <section key={section.key} data-testid={`alerts-section-${section.key}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${c.bg} ${c.border} border grid place-items-center`}>
                    <Icon size={20} weight="bold" className={c.text} />
                  </div>
                  <div>
                    <h2 className="font-display font-black tracking-tight text-xl">{section.title}</h2>
                    <div className={`text-xs ${c.text}`}>{section.subtitle} · {items.length} credential{items.length > 1 ? "s" : ""}</div>
                  </div>
                </div>
                <div className={`rounded-2xl border ${c.border} ${c.bg} divide-y divide-white/5`}>
                  {items.map((it) => (
                    <Link
                      key={it.id}
                      to={`/staff/${it.staff_id}`}
                      className="flex items-center justify-between p-4 md:p-5 hover:bg-white/[0.03] transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-100 truncate">{it.staff_name}</div>
                          <div className="text-xs text-slate-500 truncate capitalize">
                            {it.staff_role} · {it.cred_type} · Expires {format(new Date(it.expiration_date), "MMM d, yyyy")}
                          </div>
                        </div>
                      </div>
                      <ArrowRight size={16} weight="bold" className="text-slate-500" />
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
